<template>
  <div ref="root" class="alarm-overview" :style="{ '--pie-scale': pieScale }">
    <div class="section-header">
      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
      <h3>{{ title || '监控告警总览' }}</h3>
    </div>

    <div class="chart-block">
      <div class="chart-title">分类分布</div>
      <div v-if="bars.length" class="bar-chart">
        <div class="y-axis">
          <span>{{ yMax }}</span>
          <span>{{ yMid }}</span>
          <span>0</span>
        </div>
        <div class="plot">
          <div class="bars">
            <div v-for="(bar, index) in bars" :key="index" class="bar-item">
              <span class="bar-value">{{ bar.value }}</span>
              <div class="bar-track">
                <div class="bar-fill" :style="{ height: bar.percent + '%', background: bar.color }"></div>
              </div>
            </div>
          </div>
          <div class="x-labels">
            <span v-for="(bar, index) in bars" :key="index" :title="bar.label">{{ bar.label }}</span>
          </div>
        </div>
      </div>
      <div v-else class="empty">暂无事件</div>
    </div>

    <div class="pie-row">
      <div class="pie-card">
        <div class="chart-title">状态统计</div>
        <div class="pie-content">
          <div class="donut" :style="statusPieStyle">
            <div class="donut-in">
              <b>{{ statusTotal }}</b>
              <span>事件</span>
            </div>
          </div>
          <div class="legend">
            <div v-for="item in statusItems" :key="item.key" class="legend-item">
              <i :style="{ background: item.color }"></i>
              <span>{{ item.label }}</span>
              <b>{{ item.value }}</b>
            </div>
          </div>
        </div>
      </div>

      <div class="pie-card">
        <div class="chart-title">事件等级</div>
        <div class="pie-content">
          <div class="donut" :style="levelPieStyle">
            <div class="donut-in">
              <b>{{ levelTotal }}</b>
              <span>事件</span>
            </div>
          </div>
          <div class="legend">
            <div v-for="item in levelItems" :key="item.key" class="legend-item">
              <i :style="{ background: item.color }"></i>
              <span>{{ item.label }}</span>
              <b>{{ item.value }}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const LEVEL_COLORS = {
  CRITICAL: '#fc8181',
  ALARM: '#f6ad55',
  WARNING: '#63b3ed'
}

const BAR_COLORS = ['#63b3ed', '#48bb78', '#f6ad55', '#fc8181', '#b794f4', '#4fd1c5', '#f687b3', '#a0aec0']

export default {
  name: 'VideoAlarmOverview',
  props: {
    title: {
      type: String,
      default: ''
    },
    overview: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      pieScale: 1,
      resizeObserver: null
    }
  },
  computed: {
    bars() {
      const list = this.overview.bars || []
      const max = Math.max(...list.map(item => Number(item.value) || 0), 1)
      return list.map((item, index) => ({
        label: item.label,
        value: item.value,
        percent: Math.round((Number(item.value) || 0) * 100 / max),
        color: BAR_COLORS[index % BAR_COLORS.length]
      }))
    },
    statusItems() {
      return [
        { key: 'pending', label: '待处理', value: Number(this.overview.pending) || 0, color: '#fc8181' },
        { key: 'rectifying', label: '整改中', value: Number(this.overview.rectifying) || 0, color: '#f6ad55' },
        { key: 'rectified', label: '已整改', value: Number(this.overview.rectified) || 0, color: '#48bb78' }
      ]
    },
    statusTotal() {
      return this.statusItems.reduce((total, item) => total + item.value, 0)
    },
    statusPieStyle() {
      return this.buildPieStyle(this.statusItems, this.statusTotal)
    },
    yMax() {
      return Math.max(...this.bars.map(item => Number(item.value) || 0), 1)
    },
    yMid() {
      return Math.max(Math.round(this.yMax / 2), 1)
    },
    levelItems() {
      return (this.overview.levels || []).map(item => ({
        ...item,
        value: Number(item.value) || 0,
        color: LEVEL_COLORS[item.key] || '#a0aec0'
      }))
    },
    levelTotal() {
      return this.levelItems.reduce((total, item) => total + item.value, 0)
    },
    levelPieStyle() {
      return this.buildPieStyle(this.levelItems, this.levelTotal)
    }
  },
  methods: {
    updateScale() {
      const width = this.$el ? this.$el.clientWidth : 0
      const height = this.$el ? this.$el.clientHeight : 0
      const scale = Math.min(Math.max(Math.min(width / 560, height / 420), 1), 1.7)
      this.pieScale = Number(scale.toFixed(2))
    },
    buildPieStyle(items, total) {
      if (!total) {
        return { background: 'conic-gradient(rgba(160, 174, 192, 0.18) 0 100%)' }
      }
      let current = 0
      const parts = items
        .filter(item => item.value > 0)
        .map(item => {
          const start = current
          current += item.value
          return `${item.color} ${start * 100 / total}% ${current * 100 / total}%`
        })
      return { background: `conic-gradient(${parts.join(', ')})` }
    }
  },
  mounted() {
    this.updateScale()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateScale)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.updateScale)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateScale)
    }
  }
}
</script>

<style scoped>
.alarm-overview {
  --pie-scale: 1;
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.icon {
  width: 20px;
  height: 20px;
  color: #63b3ed;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.chart-block {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chart-title {
  flex-shrink: 0;
  font-size: 13px;
  color: #a0aec0;
  margin-bottom: 6px;
}

.bar-chart {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 6px;
  padding-top: 4px;
}

.y-axis {
  flex: 0 0 22px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 18px;
  font-size: 10px;
  color: #a0aec0;
  text-align: right;
}

.plot {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.bars {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 0 6px;
  border-left: 1px solid rgba(99, 179, 237, 0.35);
  border-bottom: 1px solid rgba(99, 179, 237, 0.35);
}

.bar-item {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.bar-value {
  font-size: 11px;
  font-family: "Bahnschrift", "DIN Alternate", sans-serif;
  color: #e2e8f0;
  flex-shrink: 0;
}

.bar-track {
  flex: 1;
  width: 12px;
  min-height: 16px;
  display: flex;
  align-items: flex-end;
  background: rgba(99, 179, 237, 0.08);
  border-radius: 4px 4px 0 0;
  overflow: hidden;
}

.bar-fill {
  width: 100%;
  border-radius: 4px 4px 0 0;
  background: #63b3ed;
  min-height: 2px;
  transition: height 0.5s ease;
}

.x-labels {
  flex-shrink: 0;
  display: flex;
  gap: 6px;
  padding: 4px 6px 0;
}

.x-labels span {
  flex: 1;
  min-width: 0;
  font-size: 10px;
  color: #a0aec0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pie-row {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
  min-height: 0;
}

.pie-card {
  min-width: 0;
  padding: calc(10px * var(--pie-scale));
  border-radius: 8px;
  background: rgba(49, 130, 206, 0.08);
  display: flex;
  flex-direction: column;
}

.pie-card .chart-title {
  margin-bottom: calc(4px * var(--pie-scale));
  font-size: calc(13px * var(--pie-scale));
}

.pie-content {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(12px * var(--pie-scale));
}

.donut {
  position: relative;
  flex: 0 0 calc(82px * var(--pie-scale));
  width: calc(82px * var(--pie-scale));
  height: calc(82px * var(--pie-scale));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-in {
  width: calc(60px * var(--pie-scale));
  height: calc(60px * var(--pie-scale));
  border-radius: 50%;
  background: #0d2540;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.donut-in b {
  font-size: calc(18px * var(--pie-scale));
  font-family: "Bahnschrift", "DIN Alternate", sans-serif;
  color: #e2e8f0;
  line-height: 1;
}

.donut-in span {
  margin-top: 2px;
  font-size: calc(10px * var(--pie-scale));
  color: #a0aec0;
}

.legend {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: calc(6px * var(--pie-scale));
}

.legend-item {
  display: flex;
  align-items: center;
  gap: calc(6px * var(--pie-scale));
  font-size: calc(12px * var(--pie-scale));
  color: #a0aec0;
  white-space: nowrap;
}

.legend-item i {
  width: calc(7px * var(--pie-scale));
  height: calc(7px * var(--pie-scale));
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-item b {
  margin-left: 0;
  font-family: "Bahnschrift", "DIN Alternate", sans-serif;
  color: #e2e8f0;
}

.empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #718096;
  background: rgba(16, 38, 70, 0.35);
  border-radius: 8px;
}
</style>
